import { useRequestReservationChange } from "@/api/admin/useRequestReservationChange";
import { Button, Divider, Group, Modal, ScrollArea, Stack, Text, Title } from "@mantine/core";
import { useState } from "react";
import { showErrorNotification, showSuccessNotification } from "../utils/errorNotifications";
import { CurrentReservationDisplay } from "./CurrentReservationDisplay";
import { EventSelectionList } from "./EventSelectionList";
import { ChangeRequestModalProps } from "./types";

export const ChangeRequestModal = ({
  opened,
  onClose,
  reservation,
  currentEvent,
  availableEvents,
}: ChangeRequestModalProps) => {
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
  const requestChange = useRequestReservationChange();

  // Filter out current event from available events
  const filteredEvents = availableEvents.filter((event) => event.id !== currentEvent.id);

  const handleConfirm = async () => {
    if (!selectedEventId) return;

    try {
      await requestChange.mutateAsync({
        reservationId: reservation.id,
        newEventId: selectedEventId,
      });

      showSuccessNotification(
        "변경 요청 완료",
        `${reservation.user.name}님에게 이벤트 변경을 제안했습니다`
      );

      onClose();
      setSelectedEventId(null);
    } catch (error) {
      showErrorNotification(error, "변경 요청 실패");
    }
  };

  const handleClose = () => {
    setSelectedEventId(null);
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Title order={3} c="black">
          일정 변경 요청
        </Title>
      }
      size="xl"
      scrollAreaComponent={ScrollArea.Autosize}
    >
      <Stack gap="lg">
        {/* Current Reservation Display */}
        <CurrentReservationDisplay
          request={reservation}
          currentEventTime={currentEvent.startTime}
          currentEventLocation={currentEvent.locationName}
        />

        <Divider />

        {/* Event Selection */}
        <div>
          <Title order={4} c="black" mb="md">
            변경 요청할 이벤트 선택
          </Title>
          <Text size="sm" c="dimmed" mb="md">
            아래 이벤트 중 하나를 선택하여 참석자에게 변경을 제안할 수 있습니다
          </Text>
          <EventSelectionList
            events={filteredEvents}
            selectedEventId={selectedEventId}
            onSelectEvent={setSelectedEventId}
          />
        </div>

        {/* Action Buttons */}
        <Group justify="flex-end" gap="sm">
          <Button variant="outline" onClick={handleClose}>
            취소
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!selectedEventId}
            loading={requestChange.isPending}
          >
            이 이벤트로 변경 요청하기
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
